import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { publicApi } from '../../lib/api'
import { usePageMeta } from '../../hooks/usePageMeta'
import { Button } from '../../components/ui/button'
import { ArrowLeft, Loader2, Calendar, MapPin } from 'lucide-react'

// Public, read-only events list for a parish. Adapted from the authenticated
// donor ParishEvents page: fetches through publicApi regardless of session,
// has no RSVP affordances (the public payload carries no RSVP data and
// RSVPing requires an account), and navigates to the public event detail URL.
export default function PublicParishEvents() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(1)
  const [meta, setMeta] = useState(null)
  const limit = 12

  usePageMeta({
    title: 'Parish Events — Claritas',
    description: 'See upcoming events at this parish.',
  })

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError(null)
      try {
        const response = await publicApi.getParishEvents(id, { page, limit })
        if (cancelled) return
        setEvents(response.data || [])
        setMeta(response.meta || null)
      } catch (err) {
        if (cancelled) return
        console.error('Failed to fetch events:', err)
        setError(err.message || 'Failed to load events')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    if (id) load()
    return () => {
      cancelled = true
    }
  }, [id, page])

  const formatDate = (dateString) => {
    if (!dateString) return null
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-10 space-y-6">
      {/* Header */}
      <div>
        <Button
          variant="ghost"
          onClick={() => navigate(`/parishes/${id}`)}
          className="text-charcoal hover:text-charcoal-muted mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Parish
        </Button>
        <h1 className="text-3xl font-bold font-display text-charcoal">Parish Events</h1>
        <p className="text-sm text-charcoal-muted mt-2">
          <Link to="/sign-in" className="text-gold font-semibold">
            Sign in to RSVP
          </Link>{' '}
          to events.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 text-gold animate-spin" />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded p-6">
          <p className="text-red-600">{error}</p>
        </div>
      ) : events.length === 0 ? (
        <div className="bg-white border border-surface-border rounded p-8 text-center">
          <Calendar className="w-12 h-12 text-charcoal-faint mx-auto mb-3" />
          <p className="text-charcoal-muted">No events found for this parish.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((event) => {
              const nextOccurrence = event.upcomingOccurrences?.[0]
              return (
                <div
                  key={event.id}
                  className="bg-white border border-surface-border rounded p-6 cursor-pointer transition-all duration-200 hover:border-gold/30 hover:shadow-subtle"
                  onClick={() => navigate(`/parishes/${id}/events/${event.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      navigate(`/parishes/${id}/events/${event.id}`)
                    }
                  }}
                >
                  <h3 className="text-xl font-bold font-display text-charcoal mb-2 line-clamp-2">
                    {event.title}
                  </h3>

                  {nextOccurrence && (
                    <div className="flex items-center gap-2 text-charcoal-muted mb-2">
                      <Calendar className="w-4 h-4 flex-shrink-0 text-charcoal-subtle" />
                      <span className="text-sm">{formatDate(nextOccurrence.startTime)}</span>
                    </div>
                  )}

                  {event.location && (
                    <div className="flex items-center gap-2 text-charcoal-muted">
                      <MapPin className="w-4 h-4 flex-shrink-0 text-charcoal-subtle" />
                      <span className="text-sm line-clamp-1">{event.location}</span>
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Pagination */}
          {meta && (meta.hasNextPage || page > 1) && (
            <div className="flex items-center justify-center gap-4 pt-4">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <span className="text-sm text-charcoal-muted">
                Page {page}
                {meta.totalPages ? ` of ${meta.totalPages}` : ''}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={!meta.hasNextPage}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
